/**
 * Flying Points Animation Module
 * Animates a points badge from quiz result message to achievement panel
 */

import {EASING_CURVES} from './motion/easing.js';
import {t} from '../localization/translate.js';

/**
 * Animate progress bar growth and color change
 * @param {number} points - The number of points to add
 * @returns {() => void} - Function to revert colors back to original
 */
function animateProgressBar(points) {
    // Find the progress bar using the new js-achievementProgressBar class
    /** @type {HTMLElement|null} */
    const progressBar = document.querySelector('.js-achievementProgressBar');
    if (!progressBar) {
        console.warn('Progress bar not found (.js-achievementProgressBar), skipping bar animation');
        return () => {};
    }

    // Get score information from data attributes
    const scoreElement = document.querySelector('.js-currentScore');
    if (!scoreElement) {
        console.warn('Score element not found (.js-currentScore), skipping bar animation');
        return () => {};
    }

    const currentScore = parseFloat(scoreElement.getAttribute('data-current-score') || '0');
    const fullScore = parseFloat(scoreElement.getAttribute('data-full-score') || '100');
    const newScore = currentScore + points;
    const newPercentage = (newScore / fullScore) * 100;

    // Find ALL circle connectors with red borders (before current progress)
    /** @type {HTMLElement[]} */
    const circleConnectors = [...document.querySelectorAll('.js-progressCircle')].filter(circle => {
        const hasRedBorder = circle.classList.contains('fg-border-brand-default');
        return hasRedBorder;
    });

    // Temporarily change progress bar and connectors to success colors
    progressBar.style.transition = `width 0.8s ${EASING_CURVES['ease-out-expo']}, background-color 0.3s ease`;
    progressBar.classList.remove('fg-surface-brand-default');
    progressBar.classList.add('fg-surface-status-success-default');
    progressBar.style.width = `${newPercentage}%`;
    progressBar.setAttribute('aria-valuenow', String(newPercentage));
    progressBar.setAttribute('data-percentage', String(newPercentage));

    // Change primary-bordered circle connectors to success border color with transition
    circleConnectors.forEach((connector) => {
        /** @type {HTMLElement} */
        const element = /** @type {HTMLElement} */ (connector);
        element.style.transition = 'border-color 0.3s ease';
        element.classList.remove('fg-border-brand-default');
        element.classList.add('border-green-700');
    });

    // Return function to revert colors
    return () => {
        progressBar.classList.remove('fg-surface-status-success-default');
        progressBar.classList.add('fg-surface-brand-default');

        // Revert circle connectors back to primary
        circleConnectors.forEach((connector) => {
            connector.classList.remove('border-green-700');
            connector.classList.add('fg-border-brand-default');
        });
    };
}

/**
 * Create and animate a flying points badge from the result message area
 * @param {number} points - The number of points to display
 * @param {HTMLElement} staticBadge - The static badge element to animate from
 */
function animateFlyingPointsFromBadge(points, staticBadge) {
    // Find the target span in achievement panel
    const targetSpan = document.querySelector('.js-addedPoints');
    if (!targetSpan) {
        console.warn('Target span .js-addedPoints not found, skipping points animation');
        return;
    }

    const startRect = staticBadge.getBoundingClientRect();
    const endRect = targetSpan.getBoundingClientRect();

    // Create flying badge starting from the static badge position (already at scale 1)
    const flyingBadge = document.createElement('div');
    flyingBadge.textContent = t('+:count Point|+:count Points', {}, points);

    flyingBadge.className = [
        'fixed',
        'z-[9999]',
        'pointer-events-none',
        'font-bold',
        'fg-body-sm',
        'px-6',
        'lg:px-8',
        'fg-rounded-md',
        'fg-surface-status-success-default',
        'fg-text-neutral-fixed-default-inverse',
        'whitespace-nowrap',
    ].join(' ');

    // Start at the static badge position, already at full scale
    flyingBadge.style.left = `${startRect.left}px`;
    flyingBadge.style.top = `${startRect.top}px`;
    flyingBadge.style.transform = 'scale(1)';
    flyingBadge.style.opacity = '1';
    flyingBadge.style.transition = 'none';

    document.body.append(flyingBadge);

    // Hide the static badge
    staticBadge.style.opacity = '0';

    // Start flying animation immediately
    requestAnimationFrame(() => {
        flyingBadge.style.transition = `all 2s ${EASING_CURVES['ease-in-out']}`;
        flyingBadge.style.left = `${endRect.left}px`;
        flyingBadge.style.top = `${endRect.top}px`;
    });

    // After flying completes, convert to inline element and park
    setTimeout(() => {
        // Animate progress bar when badge arrives and get revert function
        const revertProgressColors = animateProgressBar(points);

        // Convert to inline element
        flyingBadge.className = [
            'inline-flex',
            'items-center',
            'justify-center',
            'font-bold',
            'fg-body-sm',
            'px-6',
            'lg:px-8',
            'fg-rounded-md',
            'fg-surface-status-success-default',
            'fg-text-neutral-fixed-default-inverse',
            'whitespace-nowrap',
        ].join(' ');

        flyingBadge.style.left = '';
        flyingBadge.style.top = '';
        flyingBadge.style.transform = '';
        flyingBadge.style.opacity = '1';
        flyingBadge.style.transition = 'transform 0.3s ease, opacity 0.3s ease';

        // Park in target location
        targetSpan.append(flyingBadge);

        // Make badge disappear after 5 seconds and revert colors
        setTimeout(() => {
            flyingBadge.style.transform = 'scale(0)';
            flyingBadge.style.opacity = '0';

            // Revert progress bar and circle colors
            revertProgressColors();

            // Remove from DOM after animation completes
            setTimeout(() => {
                flyingBadge.remove();
            }, 300);
        }, 5000);
    }, 2000);
}

/**
 * Show points badge inline with the result message
 * @param {number} points - The number of points to display
 * @param {HTMLElement} resultMessageContainer - The result message container element
 * @returns {HTMLElement} - The created badge element
 */
function createInlinePointsBadge(points, resultMessageContainer) {
    const badge = document.createElement('span');
    badge.textContent = t('+:count Point|+:count Points', {}, points);
    badge.className = [
        'inline-flex',
        'items-center',
        'justify-center',
        'font-bold',
        'fg-body-sm',
        'px-6',
        'lg:px-8',
        'fg-rounded-md',
        'fg-surface-status-success-default',
        'fg-text-neutral-fixed-default-inverse',
    ].join(' ');

    // Start with scale 0 for pop animation
    badge.style.transform = 'scale(0)';
    badge.style.transition = `transform 0.5s ${EASING_CURVES['ease-out-expo']}`;

    resultMessageContainer.append(badge);

    // Trigger scale-up animation with ease-out-expo
    requestAnimationFrame(() => {
        badge.style.transform = 'scale(1)';
    });

    return badge;
}

/**
 * Trigger the complete points animation sequence
 * @param {number} points - The number of points earned
 * @param {HTMLElement} resultMessageContainer - The quiz result message container (quizResult__text)
 */
export async function triggerPointsAnimation(points, resultMessageContainer) {
    if (points <= 0) {
        return;
    }

    // Wait a bit for the result message to be visible
    await new Promise(resolve => setTimeout(resolve, 300));

    // Create and show badge inline with result message (with scale-up animation)
    const staticBadge = createInlinePointsBadge(points, resultMessageContainer);

    // Wait for user to see the badge
    await new Promise(resolve => setTimeout(resolve, 800));

    // Animate badge flying to achievement panel
    animateFlyingPointsFromBadge(points, staticBadge);
}
